// @flow

// HTTP status of the Sentry request for a crash event, shown on the crash screen.
// Only the real response code is stored: a missing response proves nothing,
// so the screen shows PENDING until the code arrives and UNKNOWN after the timeout.

export const PENDING_STATUS = '…'
export const UNKNOWN_STATUS = 'UNKNOWN'
export const SEND_STATUS_TIMEOUT_MS = 10000

const MAX_EVENTS = 20

type Listener = (status: string) => void

const statuses: Map<string, string> = new Map()
const listeners: Map<string, Array<Listener>> = new Map()
const registeredClients: WeakSet<Object> = new WeakSet()

export const getSendStatus = (eventId: string): string => statuses.get(eventId) || ''

export const setSendStatus = (eventId: string, status: string) => {
  statuses.delete(eventId)
  statuses.set(eventId, status)

  if (statuses.size > MAX_EVENTS) {
    statuses.delete(statuses.keys().next().value)
  }

  ;(listeners.get(eventId) || []).forEach((listener) => listener(status))
}

export const subscribeSendStatus = (eventId: string, listener: Listener): (() => void) => {
  listeners.set(eventId, (listeners.get(eventId) || []).concat(listener))

  return () => {
    const rest = (listeners.get(eventId) || []).filter((item) => item !== listener)

    if (rest.length) listeners.set(eventId, rest)
    else listeners.delete(eventId)
  }
}

// Called once after Sentry.init; a response may arrive before the crash screen mounts,
// so it is stored here rather than in the component
// eslint-disable-next-line complexity
export const registerSentryClient = (client: ?Object) => {
  if (!client || typeof client.on !== 'function' || registeredClients.has(client)) return

  registeredClients.add(client)

  // eslint-disable-next-line complexity
  client.on('afterSendEvent', (event, response) => {
    if (event && event.event_id && response && response.statusCode != null) {
      setSendStatus(event.event_id, String(response.statusCode))
    }
  })
}

export const resetSendStatuses = () => {
  statuses.clear()
  listeners.clear()
}
